import React, { Component } from 'react';
import { Error } from '@shootsta/common-react';
import type { ReactNode } from 'react';
import { captureError, addBreadcrumb } from '../../config/sentry';

type ErrorBoundaryState = {
  error?: Error;
  errorInfo?: React.ErrorInfo;
};

type ErrorBoundaryProps = {
  children: ReactNode;
};

export default class ErrorBoundary extends Component<
  ErrorBoundaryProps,
  ErrorBoundaryState
> {
  constructor(props: ErrorBoundaryProps) {
    super(props);

    this.state = {};
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    this.setState({ error, errorInfo });

    // Capture error with Sentry in production
    this.captureErrorWithSentry(error, errorInfo);
  }

  /**
   * Capture error with Sentry including additional context
   */
  private captureErrorWithSentry = (
    error: Error,
    errorInfo: React.ErrorInfo
  ) => {
    try {
      // Add breadcrumb for error boundary trigger
      addBreadcrumb('Error boundary triggered', 'error', 'error', {
        errorMessage: error.message,
        errorStack: error.stack,
        componentStack: errorInfo.componentStack
      });

      // Capture the error with Sentry (context is handled centrally)
      // Only add error boundary specific context
      const errorBoundaryContext = {
        errorBoundary: {
          componentStack: errorInfo.componentStack,
          errorMessage: error.message,
          errorStack: error.stack,
          timestamp: new Date().toISOString()
        }
      };

      captureError(error, errorBoundaryContext);
    } catch (sentryError) {
      console.error('Failed to capture error with Sentry:', sentryError);
    }
  };

  render() {
    const { error } = this.state;
    const { children } = this.props;

    if (!error) {
      return children;
    }

    return <Error />;
  }
}
